import { useState, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
// TypographyTogglePanel is owner/dev-only — lazy-load it to keep it out of the
// landing-page critical bundle. Mounted globally via <TypographyTogglePanel />.
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { MfaProvider, useMfa } from "@/contexts/MfaContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { VisibilityProvider } from "@/contexts/VisibilityContext";
import { lazy, Suspense } from "react";
import { AiLoader } from "@/components/AiLoader";
import { ScrollToTop } from "@/components/ScrollToTop";
import { ErrorBoundary } from "@/components/ErrorBoundary";
const Auth = lazy(() => import("./pages/Auth"));
const TypographyDiagnosticsPage = lazy(() => import("./pages/TypographyDiagnosticsPage"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PendingApprovalPage = lazy(() => import("./pages/PendingApprovalPage"));
const UnsubscribePage = lazy(() => import("./pages/UnsubscribePage"));
const CookieConsentBanner = lazy(() => import("@/components/legal/CookieConsentBanner").then(m => ({ default: m.CookieConsentBanner })));
import { CartProvider } from "@/contexts/CartContext";
import { InterfaceModeProvider } from "@/contexts/InterfaceModeContext";
import { AdminModeGate } from "@/components/AdminModeGate";
import { CountryBlockGuard } from "@/components/CountryBlockGuard";
import { StoreProvider } from "@/contexts/StoreContext";
import { SourceFilterProvider } from "@/hooks/useSourceFilter";
import { useStoreDomain } from "@/hooks/useStoreDomain";
import { useSessionTracker } from "@/hooks/useSessionTracker";
import { RoutedContent } from "@/components/GlobalAccessLock";
import { getPostLoginRedirect } from "@/lib/post-login-redirect";


// Wrapper that can be used inside routes (AdminModeGate needs context providers)
function AdminModeGateWrapper({ children }: { children: React.ReactNode }) {
  return <AdminModeGate>{children}</AdminModeGate>;
}

// Tracks the active session for the Security panel (must live inside AuthProvider)
function SessionTrackerMount() {
  useSessionTracker();
  return null;
}

// Preload dashboard chunks only AFTER a session exists. Anonymous landing-page
// visitors never download these (saved ~108 kB raw / ~26 kB gzip).
function AuthedPreloader() {
  const { user } = useAuth();
  useEffect(() => {
    if (!user) return;
    const ric: any = (window as any).requestIdleCallback || ((cb: any) => setTimeout(cb, 1500));
    const handle = ric(() => {
      import("./pages/LeadsPage");
      import("./pages/CommerceOrdersPage");
    });
    return () => {
      const cic: any = (window as any).cancelIdleCallback;
      if (cic && typeof handle === "number") cic(handle);
    };
  }, [user]);
  return null;
}

// Surface a toast when an impersonation session was force-ended because the
// target seller's subscription/trial expired. The redirect carries
// `?impersonation_ended=expired` (or `=ended`); we read it once, toast, then
// strip the param so refreshes don't re-fire.
function ImpersonationEndedToast() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const reason = params.get("impersonation_ended");
    if (!reason) return;

    if (reason === "expired") {
      toast.warning("Impersonation ended", {
        description:
          "The seller's subscription has expired, so you've been returned to your own account.",
        duration: 7000,
      });
    } else {
      toast("Impersonation ended");
    }

    const next = new URLSearchParams(params);
    next.delete("impersonation_ended");
    navigate(
      { pathname: location.pathname, search: next.toString() ? `?${next.toString()}` : "" },
      { replace: true },
    );
  }, [params, navigate, location]);

  return null;
}



// Lazy-load dashboard layout — only needed for authenticated users
const DashboardLayout = lazy(() => import("./components/shells/ShellRouter"));
const DashboardHomePage = lazy(() => import("./pages/DashboardHomePage"));

// NOTE: page preloading for authenticated routes (Leads / CommerceOrders) is
// gated on a real session inside AuthProvider — it must not run for anonymous
// landing-page visitors, who never need those chunks.
const TypographyTogglePanel = lazy(() =>
  import("@/components/dev/TypographyTogglePanel").then(m => ({ default: m.TypographyTogglePanel }))
);
// Lazy-loaded pages
const DiscoveryPage = lazy(() => import("./pages/DiscoveryPage"));

const CreativeStudioPage = lazy(() => import("./pages/CreativeStudioPage"));
const AdsPage = lazy(() => import("./pages/AdsPage"));
const InventoryPage = lazy(() => import("./pages/InventoryPage"));
const PerformancePage = lazy(() => import("./pages/PerformancePage"));
const ReorderPage = lazy(() => import("./pages/ReorderPage"));
const GlobalStatisticsPage = lazy(() => import("./pages/GlobalStatisticsPage"));
const ProfitSimulatorPage = lazy(() => import("./pages/ProfitSimulatorPage"));

// AI Intelligence pages
const MarketIntelligencePage = lazy(() => import("./pages/MarketIntelligencePage"));
const DeliveryRiskIntelligencePage = lazy(() => import("./pages/DeliveryRiskIntelligencePage"));






const StoreConnectionsPage = lazy(() => import("./pages/StoreConnectionsPage"));
const SmartStorePage = lazy(() => import("./pages/SmartStorePage"));
const SmartStorePublicPage = lazy(() => import("./pages/SmartStorePublicPage"));
const StoreSubdomainResolver = lazy(() => import("./pages/StoreSubdomainResolver"));
const IntegrationsHubPage = lazy(() => import("./pages/IntegrationsHubPage"));
const LeadsPage = lazy(() => import("./pages/LeadsPage"));
const AgentSettingsPage = lazy(() => import("./pages/AgentSettingsPage"));
const UserSettingsPage = lazy(() => import("./pages/UserSettingsPage"));

const CommerceOrdersPage = lazy(() => import("./pages/CommerceOrdersPage"));
const ShopifyStorePage = lazy(() => import("./pages/ShopifyStorePage"));
const ShopifyProductPage = lazy(() => import("./pages/ShopifyProductPage"));


const Team = lazy(() => import("./pages/Team"));
const Pricing = lazy(() => import("./pages/Pricing"));
const AffiliateDashboard = lazy(() => import("./pages/AffiliateDashboard"));
const AdminCommandCenter = lazy(() => import("./pages/AdminCommandCenter"));
const PricingEconomicsPage = lazy(() => import("./pages/PricingEconomicsPage"));
const DataExportPage = lazy(() => import("./pages/DataExportPage"));
const ProductReportPage = lazy(() => import("./pages/ProductReportPage"));
const ProductReportDetailPage = lazy(() => import("./pages/ProductReportDetailPage"));
const AutoModeSettingsPage = lazy(() => import("./pages/AutoModeSettingsPage"));
const ReportsCalendarPage = lazy(() => import("./pages/ReportsCalendarPage"));
const HistoryWalletPage = lazy(() => import("./pages/HistoryWalletPage"));

const Settings = lazy(() => import("./pages/Settings"));
const SecretsManagerPage = lazy(() => import("./pages/SecretsManagerPage"));
const MoneyDashboardPage = lazy(() => import("./pages/MoneyDashboardPage"));
const DecisionsPage = lazy(() => import("./pages/DecisionsPage"));
const AlertsPage = lazy(() => import("./pages/AlertsPage"));
const DailyReportPage = lazy(() => import("./pages/DailyReportPage"));
const AiLearningImportPage = lazy(() => import("./pages/AiLearningImportPage"));

const FulfillmentIntelligencePage = lazy(() => import("./pages/FulfillmentIntelligencePage"));
const FulfillmentMonitorPage = lazy(() => import("./pages/FulfillmentMonitorPage"));
const SyncStatusPage = lazy(() => import("./pages/SyncStatusPage"));
// QuickLaunch now redirects to Creative Studio


const AiActionsPage = lazy(() => import("./pages/AiActionsPage"));
const AutonomousModePage = lazy(() => import("./pages/AutonomousModePage"));
const OperationalTimelinePage = lazy(() => import("./pages/OperationalTimelinePage"));

const BenchmarkPage = lazy(() => import("./pages/BenchmarkPage"));
const ExperimentationPage = lazy(() => import("./pages/ExperimentationLabPage"));
const UserActivityMapPage = lazy(() => import("./pages/UserActivityMapPage"));
const ConversionFunnelPage = lazy(() => import("./pages/ConversionFunnelPage"));
const RetentionDashboardPage = lazy(() => import("./pages/RetentionDashboardPage"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const SessionReplayPage = lazy(() => import("./pages/SessionReplayPage"));
const OnboardingPage = lazy(() => import("./pages/OnboardingPage"));
const SellerOnboardingProfilePage = lazy(() => import("./pages/SellerOnboardingProfilePage"));
const UserSuccessScorePage = lazy(() => import("./pages/UserSuccessScorePage"));
const PlatformChangelogPage = lazy(() => import("./pages/PlatformChangelogPage"));
const SupportPage = lazy(() => import("./pages/SupportPage"));
const GuardianAuditPage = lazy(() => import("./pages/GuardianAuditPage"));
const AdminSecurityReportPage = lazy(() => import("./pages/AdminSecurityReportPage"));
const SellerIntelligencePage = lazy(() => import("./pages/SellerIntelligencePage"));
const SecurityApiControlPage = lazy(() => import("./pages/SecurityApiControlPage"));

const MfaChallenge = lazy(() => import("@/components/auth/MfaChallenge").then(m => ({ default: m.MfaChallenge })));
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 1,
    },
  },
});

function PageLoader() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <AiLoader text="Loading…" />
    </div>
  );
}

function ProtectedRoute({ children, allowExpired = false, ownerOnly = false, agentOnly = false, skipOnboarding = false }: { children: React.ReactNode; allowExpired?: boolean; ownerOnly?: boolean; agentOnly?: boolean; skipOnboarding?: boolean }) {
  const { user, loading, isTrialActive, subscriptionPlan, effectiveRole, accountStatus, impersonation, signOut, isAccessLocked, endImpersonation } = useAuth();
  const { mfaPending, mfaSetupRequired, mfaMethod, markVerified, checking } = useMfa();
  const role = effectiveRole;
  const location = useLocation();

  // Onboarding check for seller users
  const [onboardingChecked, setOnboardingChecked] = useState(false);
  const [onboardingCompleted, setOnboardingCompleted] = useState(true);
  const [profileCompleted, setProfileCompleted] = useState(true);

  useEffect(() => {
    if (!user || !role) return;
    // Owners/agents skip onboarding
    if (role === "owner" || role === "primary_owner" || role === "agent") {
      setOnboardingChecked(true);
      setOnboardingCompleted(true);
      setProfileCompleted(true);
      return;
    }
    Promise.all([
      supabase.from("profiles").select("onboarding_completed").eq("id", user.id).single(),
      (supabase as any).from("seller_profiles").select("onboarding_completed_at").eq("user_id", user.id).maybeSingle(),
    ]).then(([profileRes, sellerRes]) => {
      setOnboardingCompleted(profileRes.data?.onboarding_completed ?? true);
      setProfileCompleted(!!sellerRes.data?.onboarding_completed_at);
      setOnboardingChecked(true);
    });
  }, [user, role]);

  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/auth" replace />;
  // Role may still be loading after sign-in; show loader instead of redirecting to avoid loop
  if (!role) return <PageLoader />;

  // MFA challenge gate
  if (checking) return <PageLoader />;
  if (mfaPending) {
    return (
      <Suspense fallback={<PageLoader />}>
        <MfaChallenge onVerified={markVerified} onCancel={() => signOut()} setupRequired={mfaSetupRequired} initialMethod={mfaMethod} />
      </Suspense>
    );
  }

  // Sellers-only enforcement on onboarding routes — staff bypass entirely.
  if (
    (location.pathname === "/onboarding" || location.pathname === "/onboarding/profile") &&
    role !== "user"
  ) {
    return <Navigate to={getPostLoginRedirect(role)} replace />;
  }

  // Owner-only route guard (primary_owner + owner)
  if (ownerOnly && role !== "owner" && role !== "primary_owner") return <Navigate to="/" replace />;

  // Agent-only route guard (owner & primary_owner can also access)
  if (agentOnly && role !== "agent" && role !== "owner" && role !== "primary_owner") return <Navigate to="/dashboard" replace />;

  // Block pending/rejected users (owners & primary_owner & agents bypass)
  if (role !== "owner" && role !== "primary_owner" && role !== "agent") {
    if (accountStatus === "pending_approval" || accountStatus === "rejected") {
      return <Navigate to="/pending-approval" replace />;
    }
    if (accountStatus && accountStatus !== "approved") {
      return <Navigate to="/pending-approval" replace />;
    }
  }

  // ──────────────────────────────────────────────────────────────────────
  // EXPIRED-SELLER ACCESS LOCK
  // Sellers (role=user) with no active subscription AND no active trial are
  // blocked from the platform.
  //  - Direct seller login → forcibly redirected to /pricing.
  //  - Admin impersonating an expired seller → impersonation is auto-ended
  //    and the admin returns to their own dashboard. We do NOT trap the
  //    admin on the seller's pricing page.
  // The redirect happens BEFORE the dashboard layout renders, so no
  // sidebar / data flash.
  // ──────────────────────────────────────────────────────────────────────
  if (isAccessLocked && role === "user") {
    if (impersonation.active) {
      // Fire-and-forget: end the impersonation session and bounce the admin
      // back to their own dashboard. Render a loader while it resolves.
      void endImpersonation("subscription_expired").then(() => {
        if (typeof window !== "undefined") {
          window.location.replace("/dashboard?impersonation_ended=expired");
        }
      });
      return <PageLoader />;
    }
    if (location.pathname !== "/pricing" && location.pathname !== "/onboarding") {
      return <Navigate to="/pricing" replace state={{ reason: "subscription_expired" }} />;
    }
  }

  // Onboarding guard: redirect seller users who haven't completed onboarding
  if (!skipOnboarding && onboardingChecked && !onboardingCompleted && location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }

  // Profile-intelligence guard: seller users who finished operational onboarding
  // but haven't completed the profile wizard are routed there once.
  if (
    !skipOnboarding &&
    onboardingChecked &&
    onboardingCompleted &&
    !profileCompleted &&
    role === "user" &&
    location.pathname !== "/onboarding/profile"
  ) {
    return <Navigate to="/onboarding/profile" replace />;
  }

  // Primary Owner & Super Admin always bypass trial/subscription
  if (role === "owner" || role === "primary_owner") return <>{children}</>;

  // Admin bypasses trial/subscription
  if (role === "agent") return <>{children}</>;

  // During impersonation, bypass trial/subscription checks (owner is still behind the scenes)
  if (impersonation.active) return <>{children}</>;

  // Wait for onboarding check
  if (!onboardingChecked) return <PageLoader />;

  // User: expired trial users are NOT redirected — they stay in read-only mode.
  // The TrialBanner shows "Access Restricted" and TrialExpiredOverlay gates power routes.
  // Database RLS provides the final enforcement layer (see is_trial_expired()).

  return <>{children}</>;
}

function HomeRoute() {
  const { user, loading, effectiveRole, accountStatus, isAccessLocked, impersonation, endImpersonation } = useAuth();
  const role = effectiveRole;
  if (loading) return <PageLoader />;
  if (!user) return <Auth />;
  // Redirect pending/rejected users to approval page
  if (role === "user" && accountStatus && accountStatus !== "approved") {
    return <Navigate to="/pending-approval" replace />;
  }
  // Locked seller via impersonation → end the session and bounce admin home.
  if (role === "user" && isAccessLocked && impersonation.active) {
    void endImpersonation("subscription_expired").then(() => {
      if (typeof window !== "undefined") {
        window.location.replace("/dashboard?impersonation_ended=expired");
      }
    });
    return <PageLoader />;
  }
  // Locked sellers go straight to pricing — never flash the dashboard.
  if (role === "user" && isAccessLocked) {
    return <Navigate to="/pricing" replace state={{ reason: "subscription_expired" }} />;
  }
  return <Navigate to={getPostLoginRedirect(role)} replace />;
}

function AppFooter() {
  const { user, effectiveRole } = useAuth();
  if (!user) return null;
  const isManagement = effectiveRole === "primary_owner" || effectiveRole === "owner" || effectiveRole === "agent";

  return (
    <footer className="w-full py-3 border-t border-border/20">
      <div className="max-w-6xl mx-auto px-4 flex flex-col items-center gap-1.5">
        <span className="text-[11px] text-muted-foreground/70">
          Powered by <span className="font-semibold text-foreground/80">Vazoul</span>
        </span>
        {!isManagement && (
          <div className="flex items-center gap-3 text-[10px] text-muted-foreground/60">
            <Link to="/support" className="hover:text-foreground/80 transition-colors">Support</Link>
            <span className="text-border">·</span>
            <Link to="/legal/terms" className="hover:text-foreground/80 transition-colors">Terms</Link>
            <span className="text-border">·</span>
            <Link to="/legal/privacy" className="hover:text-foreground/80 transition-colors">Privacy</Link>
          </div>
        )}
      </div>
    </footer>
  );
}

function AppContent() {
  const storeDomain = useStoreDomain();

  // If this is a store domain, render the store directly — no platform UI
  if (storeDomain.isStoreDomain) {
    if (storeDomain.loading) return <PageLoader />;
    if (storeDomain.error || !storeDomain.slug) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-white">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Store Not Found</h1>
            <p className="text-gray-500">This store doesn't exist or has no published pages.</p>
          </div>
        </div>
      );
    }
    // Render store inline with BrowserRouter for navigation between store pages
    return (
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/store/:slug" element={<SmartStorePublicPage />} />
              <Route path="*" element={<SmartStorePublicPage overrideSlug={storeDomain.slug} />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </QueryClientProvider>
    );
  }

  return (
    <BrowserRouter>
      <CountryBlockGuard>
      <AuthProvider>
        <SessionTrackerMount />
        <AuthedPreloader />
        <ImpersonationEndedToast />
        <MfaProvider>
        <InterfaceModeProvider>
        <StoreProvider>
        <SourceFilterProvider>
        <CartProvider>
        
        <Suspense fallback={<PageLoader />}>
          <RoutedContent>
          <Routes>
            {/* Public Smart Store pages */}
            <Route path="/dev/typography" element={<TypographyDiagnosticsPage />} />
            <Route path="/store/:slug" element={<SmartStorePublicPage />} />
            <Route path="/s/:subdomain" element={<StoreSubdomainResolver />} />
            <Route path="/shop/:storeId" element={<StoreSubdomainResolver />} />
            {/* Public legal pages */}
            <Route path="/legal/:type" element={<LegalPage />} />

            {/* Auth */}
            <Route path="/" element={<HomeRoute />} />
            <Route path="/auth" element={<HomeRoute />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/pending-approval" element={<PendingApprovalPage />} />
            <Route path="/unsubscribe" element={<UnsubscribePage />} />
            <Route path="/onboarding" element={<ProtectedRoute skipOnboarding><OnboardingPage /></ProtectedRoute>} />
            <Route path="/onboarding/profile" element={<ProtectedRoute skipOnboarding><SellerOnboardingProfilePage /></ProtectedRoute>} />

            {/* All authenticated routes share a single DashboardLayout instance */}
            <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              {/* Core business flows */}
              <Route path="/dashboard" element={<DashboardHomePage />} />
              <Route path="/discovery" element={<DiscoveryPage />} />
              <Route path="/creative-studio" element={<CreativeStudioPage />} />
              {/* /financial removed — duplicates Product Report, Reports Calendar, History Wallet */}
              <Route path="/financial" element={<Navigate to="/reports/product" replace />} />
              <Route path="/money" element={<MoneyDashboardPage />} />
              <Route path="/alerts" element={<AlertsPage />} />
              <Route path="/reports/daily" element={<DailyReportPage />} />
              {/* /invoices fully removed — no replacement */}
              <Route path="/ads" element={<AdsPage />} />
              <Route path="/inventory" element={<InventoryPage />} />
              <Route path="/performance" element={<PerformancePage />} />
              <Route path="/reorder" element={<ReorderPage />} />
              <Route path="/global-statistics" element={<GlobalStatisticsPage />} />
              <Route path="/profit-simulator" element={<ProfitSimulatorPage />} />
              <Route path="/leads" element={<LeadsPage />} />
              <Route path="/orders" element={<CommerceOrdersPage />} />
              <Route path="/fulfillment" element={<FulfillmentIntelligencePage />} />
              <Route path="/fulfillment-monitor" element={<FulfillmentMonitorPage />} />
              <Route path="/sync-status" element={<SyncStatusPage />} />
              <Route path="/operational-timeline" element={<OperationalTimelinePage />} />
              
              <Route path="/benchmarks" element={<BenchmarkPage />} />
              <Route path="/experiments" element={<ExperimentationPage />} />
              <Route path="/support" element={<SupportPage />} />
              <Route path="/stores" element={<StoreConnectionsPage />} />
              <Route path="/smart-store" element={<SmartStorePage />} />
              <Route path="/integrations" element={<IntegrationsHubPage />} />
              <Route path="/store" element={<ShopifyStorePage />} />
              <Route path="/product/:handle" element={<ShopifyProductPage />} />
              <Route path="/user-settings" element={<UserSettingsPage />} />

              {/* AI Intelligence */}
              {/* Product Intelligence merged into Product Report */}
              <Route path="/product-intelligence" element={<Navigate to="/reports/product" replace />} />
              <Route path="/market-intelligence" element={<MarketIntelligencePage />} />
              <Route path="/delivery-risk" element={<DeliveryRiskIntelligencePage />} />
              {/* /cashflow-intelligence fully removed */}
              <Route path="/ai-actions" element={<AiActionsPage />} />
              <Route path="/autonomous" element={<AutonomousModePage />} />

              {/* Role-gated (RouteGuard enforces role visibility) */}
              <Route path="/affiliate" element={<AffiliateDashboard />} />
              <Route path="/data-export" element={<DataExportPage />} />
              <Route path="/reports/product" element={<ProductReportPage />} />
              <Route path="/reports/product/:productId" element={<ProductReportDetailPage />} />
              <Route path="/reports/product-auto-mode" element={<AutoModeSettingsPage />} />
              <Route path="/reports/calendar" element={<ReportsCalendarPage />} />
              <Route path="/reports/history-wallet" element={<HistoryWalletPage />} />
              <Route path="/agent-settings" element={<AgentSettingsPage />} />
              <Route path="/team" element={<Team />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/secrets" element={<ProtectedRoute ownerOnly><SecretsManagerPage /></ProtectedRoute>} />
              <Route path="/changelog" element={<PlatformChangelogPage />} />
              <Route path="/command-center" element={<ProtectedRoute ownerOnly><AdminCommandCenter /></ProtectedRoute>} />
              <Route path="/admin-dashboard" element={<ProtectedRoute agentOnly><DashboardHomePage /></ProtectedRoute>} />
              <Route path="/admin/pricing-economics" element={<ProtectedRoute ownerOnly><PricingEconomicsPage /></ProtectedRoute>} />
              <Route path="/guardian-audit" element={<AdminModeGateWrapper><GuardianAuditPage /></AdminModeGateWrapper>} />
              <Route path="/admin-security-report" element={<ProtectedRoute ownerOnly><AdminSecurityReportPage /></ProtectedRoute>} />
              <Route path="/admin/seller-intelligence" element={<ProtectedRoute agentOnly><SellerIntelligencePage /></ProtectedRoute>} />
              <Route path="/security-api-control" element={<ProtectedRoute ownerOnly><SecurityApiControlPage /></ProtectedRoute>} />
              <Route path="/ai-learning" element={<AdminModeGateWrapper><AiLearningImportPage /></AdminModeGateWrapper>} />
              <Route path="/activity-map" element={<AdminModeGateWrapper><UserActivityMapPage /></AdminModeGateWrapper>} />
              <Route path="/conversion-funnel" element={<AdminModeGateWrapper><ConversionFunnelPage /></AdminModeGateWrapper>} />
              <Route path="/retention" element={<AdminModeGateWrapper><RetentionDashboardPage /></AdminModeGateWrapper>} />
              <Route path="/session-replay" element={<AdminModeGateWrapper><SessionReplayPage /></AdminModeGateWrapper>} />
              <Route path="/success-scores" element={<AdminModeGateWrapper><UserSuccessScorePage /></AdminModeGateWrapper>} />

              {/* Redirects (consolidated) */}
              <Route path="/ai-agent" element={<Navigate to="/ai-actions" replace />} />
              <Route path="/ai-systems-status" element={<Navigate to="/settings" replace />} />
              <Route path="/store-connections" element={<Navigate to="/integrations?tab=stores" replace />} />
              <Route path="/shipping" element={<Navigate to="/fulfillment" replace />} />
              <Route path="/ai-tools" element={<Navigate to="/ai-actions" replace />} />
              <Route path="/generation-history" element={<Navigate to="/dashboard" replace />} />
              <Route path="/pipeline" element={<Navigate to="/fulfillment" replace />} />
              <Route path="/offer-lab" element={<Navigate to="/creative-studio" replace />} />
              <Route path="/growth-tools" element={<Navigate to="/ads" replace />} />
              <Route path="/quick-launch" element={<Navigate to="/creative-studio" replace />} />
              <Route path="/decisions" element={<DecisionsPage />} />
              <Route path="/automation" element={<Navigate to="/ai-actions" replace />} />
              <Route path="/continuous-learning" element={<Navigate to="/ai-learning" replace />} />
              <Route path="/ai-evaluation" element={<Navigate to="/ai-learning" replace />} />
            </Route>

            {/* Legacy redirects */}
            <Route path="/commerce-orders" element={<Navigate to="/orders" replace />} />
            <Route path="/statistics" element={<Navigate to="/global-statistics" replace />} />
            <Route path="/analytics" element={<Navigate to="/global-statistics" replace />} />
            <Route path="/product-research" element={<Navigate to="/discovery" replace />} />

            {/* Standalone */}
            <Route path="/pricing" element={<Pricing />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
          </RoutedContent>
        </Suspense>
        <ScrollToTop />
        <CookieConsentBanner />
        
        </CartProvider>
        </SourceFilterProvider>
        </StoreProvider>
        </InterfaceModeProvider>
        </MfaProvider>
        <AppFooter />
      </AuthProvider>
      </CountryBlockGuard>
    </BrowserRouter>
  );
}

const App = () => (
  <ErrorBoundary fallbackMessage="Vazoul encountered an unexpected error">
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
    <VisibilityProvider>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <AppContent />
      <Suspense fallback={null}>
        <TypographyTogglePanel />
      </Suspense>
    </TooltipProvider>
    </VisibilityProvider>
    </ThemeProvider>
  </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
